import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  IoMdRefresh,
  IoMdMail,
  IoMdClose,
  IoMdKeypad,
  IoMdAlert,
} from "react-icons/io";
import { TextInput } from "../../../components/ui";
import { authApi } from "../../../api/auth.api";
import "./ReactivateAccountModal.css";

export interface ReactivateResult {
  twoFactorEnabled: boolean;
  customToken: string | null;
  uid: string;
}

interface ReactivateAccountModalProps {
  isOpen: boolean;
  email: string;
  onSuccess: (result: ReactivateResult) => Promise<void> | void;
  onCancel: () => void;
}

type Step = "intro" | "codeSent" | "reactivating" | "expired" | "error";

export default function ReactivateAccountModal({
  isOpen,
  email,
  onSuccess,
  onCancel,
}: ReactivateAccountModalProps) {
  const { t, i18n } = useTranslation(["auth", "common"]);
  const [step, setStep] = useState<Step>("intro");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState<number | null>(null);
  const [deletedAt, setDeletedAt] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState("");

  const reset = () => {
    setStep("intro");
    setCode("");
    setBusy(false);
    setDaysRemaining(null);
    setDeletedAt(null);
    setErrorMsg("");
  };

  const handleClose = () => {
    reset();
    onCancel();
  };

  const requestCode = async () => {
    setErrorMsg("");
    try {
      setBusy(true);
      const result = await authApi.sendReactivationCode(email);
      const data = result.data;
      setDaysRemaining(data.daysRemaining);
      setDeletedAt(data.deletedAt);
      setStep("codeSent");
    } catch (err: any) {
      if (err?.code === "functions/failed-precondition") {
        setStep("expired");
        setErrorMsg(err.message || t("reactivate.notRecoverableGeneric"));
      } else if (err?.code === "functions/resource-exhausted") {
        setStep("error");
        setErrorMsg(err.message || t("reactivate.tooManyRequests"));
      } else if (err?.code === "functions/not-found") {
        setStep("expired");
        setErrorMsg(t("reactivate.noAccountFound"));
      } else {
        setStep("error");
        setErrorMsg(err?.message || t("reactivate.sendFailed"));
      }
    } finally {
      setBusy(false);
    }
  };

  const confirmCode = async () => {
    if (code.trim().length !== 6) {
      setErrorMsg(t("reactivate.enterSixDigit"));
      return;
    }
    setErrorMsg("");
    try {
      setBusy(true);
      setStep("reactivating");
      const result = await authApi.confirmReactivation(email, code.trim());
      await onSuccess({
        twoFactorEnabled: result.data.twoFactorEnabled,
        customToken: result.data.customToken,
        uid: result.data.uid,
      });
      reset();
    } catch (err: any) {
      setStep("codeSent");
      if (err?.code === "functions/permission-denied") {
        setErrorMsg(t("errors.invalidCode"));
      } else if (err?.code === "functions/deadline-exceeded") {
        setErrorMsg(t("errors.codeExpired"));
      } else if (err?.code === "functions/resource-exhausted") {
        setErrorMsg(t("errors.tooManyAttempts"));
      } else if (err?.code === "functions/failed-precondition") {
        setStep("expired");
        setErrorMsg(err.message || t("reactivate.notRecoverableGeneric"));
      } else {
        setErrorMsg(err?.message || t("errors.verificationFailed"));
      }
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) return null;

  const formattedDeletedAt = deletedAt
    ? new Date(deletedAt).toLocaleDateString(i18n.language === "tr" ? "tr-TR" : "en-US", { year: "numeric", month: "long", day: "numeric" })
    : null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="modal-content reactivate-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="reactivate-modal-close" onClick={handleClose}>
          <IoMdClose size={24} />
        </button>

        {step === "intro" && (
          <>
            <div className="reactivate-modal-header">
              <div className="reactivate-modal-icon">
                <IoMdRefresh size={28} />
              </div>
              <h2>{t("reactivate.introTitle")}</h2>
              <p>
                {t("reactivate.introSubtitle")}
              </p>
            </div>

            <div className="reactivate-modal-email">
              <IoMdMail size={18} />
              <span>{email}</span>
            </div>

            <p className="reactivate-modal-subtext">
              {t("reactivate.sendCodeHint")}
            </p>

            {errorMsg && (
              <div className="reactivate-modal-notice reactivate-modal-notice--error">
                <IoMdAlert size={16} />
                <span>{errorMsg}</span>
              </div>
            )}

            <button
              className="reactivate-modal-btn primary"
              onClick={requestCode}
              disabled={busy}
            >
              {busy ? (
                <>
                  <div className="spinner-small"></div>
                  {t("reactivate.sending")}
                </>
              ) : (
                t("reactivate.sendCode")
              )}
            </button>
            <button className="reactivate-modal-btn secondary" onClick={handleClose}>
              {t("common:actions.cancel")}
            </button>
          </>
        )}

        {step === "codeSent" && (
          <>
            <div className="reactivate-modal-header">
              <div className="reactivate-modal-icon">
                <IoMdKeypad size={28} />
              </div>
              <h2>{t("reactivate.codeSentTitle")}</h2>
              <p>
                {t("reactivate.codeSentSubtitle", { email })}
                {daysRemaining != null && (
                  <>
                    <br />
                    <span style={{ color: "var(--color-text-secondary)", fontSize: 13 }}>
                      {t("reactivate.daysLeft", { count: daysRemaining })}
                      {formattedDeletedAt ? t("reactivate.deletedOn", { date: formattedDeletedAt }) : "."}
                    </span>
                  </>
                )}
              </p>
            </div>

            <div className="reactivate-modal-code-container">
              <TextInput
                type="text"
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))
                }
                placeholder="000000"
                wrapperClassName="reactivate-modal-code-input"
                maxLength={6}
              />
            </div>

            {errorMsg && (
              <div className="reactivate-modal-notice reactivate-modal-notice--error">
                <IoMdAlert size={16} />
                <span>{errorMsg}</span>
              </div>
            )}

            <button
              className="reactivate-modal-btn primary"
              onClick={confirmCode}
              disabled={busy || code.length !== 6}
            >
              {busy ? (
                <>
                  <div className="spinner-small"></div>
                  {t("reactivate.reactivating")}
                </>
              ) : (
                t("reactivate.confirmReactivation")
              )}
            </button>
            <button className="reactivate-modal-btn secondary" onClick={handleClose}>
              {t("common:actions.cancel")}
            </button>
            <button
              className="reactivate-modal-resend"
              onClick={requestCode}
              disabled={busy}
            >
              {t("reactivate.resendPrompt")}
            </button>
          </>
        )}

        {step === "reactivating" && (
          <div className="reactivate-modal-header">
            <div className="spinner-large"></div>
            <h2>{t("reactivate.reactivatingTitle")}</h2>
            <p>{t("reactivate.reactivatingSubtitle")}</p>
          </div>
        )}

        {step === "expired" && (
          <>
            <div className="reactivate-modal-header">
              <div className="reactivate-modal-icon">
                <IoMdAlert size={28} />
              </div>
              <h2>{t("reactivate.notRecoverableTitle")}</h2>
              <p>
                {errorMsg || t("reactivate.notRecoverableDefault")}
              </p>
            </div>
            <button className="reactivate-modal-btn primary" onClick={handleClose}>
              {t("common:actions.close")}
            </button>
          </>
        )}

        {step === "error" && (
          <>
            <div className="reactivate-modal-header">
              <div className="reactivate-modal-icon">
                <IoMdAlert size={28} />
              </div>
              <h2>{t("reactivate.errorTitle")}</h2>
              <p>{errorMsg || t("reactivate.errorDefault")}</p>
            </div>
            <button className="reactivate-modal-btn primary" onClick={() => setStep("intro")}>
              {t("common:actions.tryAgain")}
            </button>
            <button className="reactivate-modal-btn secondary" onClick={handleClose}>
              {t("common:actions.cancel")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
